// Pure helpers for the migration wizard, kept out of the components so they can
// be tested without a DOM.
import type { MappingDraft, MigrationRun, MigrationStatus, MigrationTarget } from "./api"

/** The six wizard steps, in order. Steps 1 and 2 exist only in the browser;
 *  from step 3 on, the run on the server says where the wizard is. */
export const MIGRATION_STEPS = ["Connect", "Project", "Mapping", "Preview", "Import", "Report"] as const

export const CREATE_ON_BOARD = "__create"

const STEP_OF: Record<MigrationStatus, number> = {
  surveying: 3,
  ready: 3,
  previewing: 4,
  previewed: 4,
  queued: 5,
  running: 5,
  completed: 6,
  failed: 6,
  cancelled: 6,
}

export const stepForStatus = (status: MigrationStatus) => STEP_OF[status]

/** Statuses where the server is working and the page should keep polling. */
export const isBusy = (status: MigrationStatus) =>
  status === "surveying" || status === "previewing" || status === "queued" || status === "running"

export const isFinal = (status: MigrationStatus) =>
  status === "completed" || status === "failed" || status === "cancelled"

/** States still waiting for the admin to pick a status. These block the preview,
 *  because a guessed status would rewrite the project's history wrongly. */
export function missingChoices(mapping: MappingDraft | null): string[] {
  if (!mapping) return []
  return Object.entries(mapping.status)
    .filter(([, choice]) => !choice)
    .map(([value]) => value)
}

// The same rule as src/projects/projects.ts: 2–10 characters, an uppercase
// letter first, then uppercase letters, digits or _.
const KEY_PREFIX = /^[A-Z][A-Z0-9_]{1,9}$/

/** A key prefix suggested from the source key: the key itself when it is a
 *  valid prefix, so VOTOAMS-176 can stay VOTOAMS-176; otherwise three letters
 *  from the key, padded from the project name. */
export function suggestKeyPrefix(sourceKey: string, name = ""): string {
  const upper = sourceKey.toUpperCase()
  if (KEY_PREFIX.test(upper)) return upper
  const letters = (sourceKey + name).toUpperCase().replace(/[^A-Z]/g, "")
  return (letters + "XXX").slice(0, 3)
}

export const isValidKeyPrefix = (value: string) => KEY_PREFIX.test(value)

export function targetLabel(run: Pick<MigrationRun, "target" | "targetProject">): string {
  if (run.targetProject) return run.targetProject.key
  const target: MigrationTarget = run.target
  return "new" in target ? target.new.keyPrefix : "the project"
}

/** "29.5 MB", "4,096 MB", "820 KB". Megabytes are what the storage quota is
 *  shown in elsewhere, so they stay the unit up to a terabyte. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  const mb = bytes / (1024 * 1024)
  return `${mb < 100 ? mb.toFixed(1) : Math.round(mb).toLocaleString("en-US")} MB`
}

/** Whole and fractional hours, as the import report counts time: "747.8 h". */
export function formatHours(minutes: number): string {
  return `${(Math.round((minutes / 60) * 10) / 10).toLocaleString("en-US")} h`
}

export const formatCount = (n: number) => n.toLocaleString("en-US")

/** Splits "Open **Profile**" into plain and bold runs, for the token help. */
export function boldRuns(text: string): { text: string; bold: boolean }[] {
  return text
    .split(/(\*\*[^*]+\*\*)/)
    .filter(Boolean)
    .map((part) =>
      part.startsWith("**") && part.endsWith("**") ? { text: part.slice(2, -2), bold: true } : { text: part, bold: false },
    )
}
